import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  backNavigation,
  menuItems,
  showRequestErrors,
  subtitle,
  title,
} from '@softline/application';
import { Store } from '@softline/core';
import { DynamicModule, FieldOkResultConverter } from '@softline/dynamic';
import {
  MessageBarStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  translator,
  UiCoreModule,
  Validators,
} from '@softline/ui-core';
import { distinctUntilChanged, map, startWith, Subscription } from 'rxjs';
import { SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE } from '../../auftrag.shared';
import { Auftrag } from '../../data/auftrag';
import { Fahrzeug } from '../../data/fahrzeug';
import { Kunde } from '../../data/kunde';
import { Objekt } from '../../data/objekt';
import { Verkaufsart } from '../../data/verkaufsart';
import * as WerkstattAuftraegeStore from '../../store/werkstatt-auftraege.store';

@Component({
  selector: 'soft-erstellen',
  imports: [CommonModule, ReactiveFormsModule, DynamicModule, UiCoreModule],
  templateUrl: './erstellen.component.html',
  styleUrls: ['./erstellen.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ErstellenComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly t = translator();

  private subscription?: Subscription;
  private subscription2?: Subscription;

  readonly kundeConverter = new FieldOkResultConverter({
    id: 'id',
    number: 'number',
    '13': 'address',
    '14': 'zipCode',
    '15': 'city',
  });
  readonly verkaufsartwerkstattConverter = new FieldOkResultConverter({
    name: 'bezeichnung',
    id: 'id',
  });
  readonly fahrzeugConverter = new FieldOkResultConverter({
    id: 'id',
    name: 'bezeichnung',
  });
  readonly parameters = {
    freigabedatum: 'Alle',
    geloescht: 0,
    gesperrt: 2,
    grosskunde: '2',
    kategorie: '2,4',
    KUNDENABFRAGE_KENNZEICHEN_AKTIV: 'JA',
    lieferadresse: 0,
  };

  readonly isCreating$ = this.store.observe(
    SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE,
    WerkstattAuftraegeStore.getters.saving
  );

  readonly isCreating = toSignal(this.isCreating$);

  form = new FormGroup(
    {
      customer: new FormControl<Kunde | undefined>(undefined, Validators.required()),
      title: new FormControl(undefined, Validators.required()),
      headerText: new FormControl<string | null>(null, Validators.required()),
      sellingType: new FormControl<Verkaufsart | undefined>(
        undefined,
        Validators.required()
      ),
      object: new FormControl<Objekt | undefined>(undefined),
      vehicle: new FormControl<Fahrzeug | undefined>(undefined),
      kilometersStart: new FormControl<string | undefined>(undefined),
      operatingHours: new FormControl<string | undefined>(undefined),
    },
    { updateOn: 'blur' }
  );

  get composedTask(): any {
    const {
      title,
      customer,
      headerText,
      sellingType,
      vehicle,
      kilometersStart,
      operatingHours,
    } = this.form.getRawValue();
    return {
      id: undefined,
      number: 0,
      object: {
        id: null,
        name: title,
        number: null,
        invoiceRelease: null,
      },
      sellingType: {
        id: sellingType?.id,
        verkaufsart: sellingType?.verkaufsart,
        name: sellingType?.['bezeichnung'] ?? sellingType?.name,
      },
      mileage: +(kilometersStart ?? 0),
      description: headerText,
      customer: {
        id: customer?.id,
        kurzbez: customer?.kurzbez,
        subnummer: customer?.subnummer,
        number: customer?.['nummer'],
      },
      vehicle: { id: (vehicle as any)?.id, name: (vehicle as any)?.name },
      operatingHours: +(operatingHours || 0),
      signature: null,
    };
  }

  readonly isFormValid = toSignal(
    this.form.statusChanges.pipe(
      map((o) => o === 'VALID'),
      startWith(this.form.valid)
    ),
    { initialValue: false }
  );

  constructor() {
    title('Werkstätten Aufträge');
    subtitle(this.t('#WORKSHOP.COMPONENTS.CREATE_TASK.TITLE'));
    backNavigation(['werkstaettenauftrag']);
    menuItems(() => [
      {
        name: 'create-new-order',
        outlet: 'bottom',
        title: '#WORKSHOP.DIALOGS.CREATE_TASK.CREATE',
        icon: 'fa-regular fa-check',
        type: 'execute',
        class: 'soft-button primary',
        execute: async () => this.onSubmit(),
        canExecute: this.isFormValid() && !this.isCreating(),
      },
      {
        name: 'list',
        title: '#WORKSHOP.TASK_OUTLINE.TITLE',
        icon: 'fa-regular fa-list',
        type: 'route',
        outlet: 'nav',
        routerLink: ['/werkstaettenauftrag'],
      },
      {
        name: 'create-order',
        title: '#WORKSHOP.COMPONENTS.CREATE_TASK.TITLE',
        icon: 'fa-regular fa-pen-to-square',
        type: 'route',
        outlet: 'nav',
        routerLink: ['/werkstaettenauftrag', 'erstellen'],
      },
    ]);
  }

  async ngOnInit(): Promise<void> {
    await this.store.dispatch(
      SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE,
      WerkstattAuftraegeStore.actions.default.load,
      { data: {} }
    );
    this.subscription = this.store
      .observe(
        SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE,
        WerkstattAuftraegeStore.getters.default.default
      )
      .subscribe((template) => {
        this.form.controls.sellingType.patchValue(template?.sellingType, {
          onlySelf: false,
          emitEvent: false,
        });
        this.cdRef.markForCheck();
      });
    this.subscription2 = this.form.controls.customer.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe((customer) => {
        this.store.dispatch(
          SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE,
          WerkstattAuftraegeStore.actions.default.load,
          {
            data: {
              customer,
              sellingType: customer !== null ? this.form.value?.sellingType : null,
            },
          }
        );
      });
  }

  ngOnDestroy(): void {
    if (this.subscription && !this.subscription.closed) {
      this.subscription.unsubscribe();
    }
    if (this.subscription2 && !this.subscription2.closed) {
      this.subscription2.unsubscribe();
    }
    this.subscription = undefined;
    this.subscription2 = undefined;
  }

  async onSubmit(): Promise<void> {
    const auftrag = await this.createTask();
    if (!auftrag) {
      return;
    }
    this.form.reset();
    await this.store.dispatch(
      SOFTLINE_FEATURE_MESSAGE_BAR,
      MessageBarStore.actions.success,
      {
        title: 'Auftrag wurde erfolgreich erstellt!',
        actions: [
          {
            title: signal('Anzeigen'),
            type: 'link',
            onExecute: () => this.router.navigate(['/werkstaettenauftrag', auftrag.id]),
          },
        ],
      }
    );
  }

  private async createTask(): Promise<Auftrag | null> {
    try {
      return await this.store.dispatch(
        SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE,
        WerkstattAuftraegeStore.actions.create,
        { entity: this.composedTask }
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
    return null;
  }
}
